import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { Download, FileWarning, Loader2, X } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { toast } from "components/ui/use-toast";

import { useValidateSegmentationMutation } from "../../../../../../services/gamification";
import { type CreateGamificationFormData } from "../../../validations/gamification.validation";
import { CpfSegmentationList } from "../../../components/cpf-segmentation-list";
import {
  downloadCpfTemplate,
  toImportableCpfFile,
} from "../../../utils/cpf-spreadsheet";
import { dedupeCpfs } from "../../../utils/segmentation";
import { SEGMENTATION_COPY } from "../../../constants/messages";

const SEGMENTATION_ACCEPT =
  ".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const MAX_PREVIEW_INVALID_ROWS = 10;

export const StepSegmentation: React.FC = () => {
  const form = useFormContext<CreateGamificationFormData>();
  const [validateSegmentation, { data: report, isLoading, reset }] =
    useValidateSegmentationMutation();

  const enabled = Boolean(
    useWatch({
      control: form.control,
      name: "mechanicConfig.cpfSegmentation.enabled",
    }),
  );
  const segmentationFile = useWatch({
    control: form.control,
    name: "segmentationFile",
  });
  const segmentationCpfs = useWatch({
    control: form.control,
    name: "segmentationCpfs",
  });
  const cpfs = segmentationCpfs ?? [];

  const setCpfs = (next: string[]): void => {
    form.setValue("segmentationCpfs", next, { shouldDirty: true });
    if (next.length > 0) form.clearErrors("segmentationFile");
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    form.setValue("segmentationFile", file, { shouldDirty: true });
    form.clearErrors("segmentationFile");

    try {
      const importable = await toImportableCpfFile(file);
      const result = await validateSegmentation({ file: importable }).unwrap();
      const latest: string[] = form.getValues("segmentationCpfs") ?? [];
      setCpfs(dedupeCpfs([...latest, ...result.validCpfs]));
    } catch {
      toast({
        title: "Erro",
        description: SEGMENTATION_COPY.importFailed,
        variant: "destructive",
      });
    }

    event.target.value = "";
  };

  const handleRemoveFile = () => {
    form.setValue("segmentationFile", undefined, { shouldDirty: true });
    reset();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Segmentação de CPF</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Controller
            control={form.control}
            name="mechanicConfig.cpfSegmentation.enabled"
            render={({ field }) => (
              <Switch
                id="cpf-segmentation-enabled"
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
              />
            )}
          />
          <Label htmlFor="cpf-segmentation-enabled" className="mb-0">
            Segmentar por base de CPF?
          </Label>
        </div>

        {!enabled && (
          <p className="text-sm text-gray-500">
            Sem base de CPFs, a campanha fica aberta a todos os clientes.
          </p>
        )}

        {enabled && (
          <>
            <p className="text-sm text-gray-500">
              Importe um arquivo CSV/XLSX com os CPFs elegíveis ou monte a base à
              mão. Baixe o modelo em Excel se quiser começar do formato certo —
              os CPFs de exemplo devem ser apagados. Você pode revisar, editar e
              remover CPFs antes de criar a campanha; a base é gravada quando a
              campanha for criada.
            </p>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Label htmlFor="segmentation-file">
                  Importar arquivo (CSV ou XLSX)
                </Label>
                <Input
                  id="segmentation-file"
                  type="file"
                  accept={SEGMENTATION_ACCEPT}
                  onChange={handleFileChange}
                  disabled={isLoading}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => downloadCpfTemplate()}
              >
                <Download className="mr-2 h-4 w-4" />
                Baixar modelo (Excel)
              </Button>
              {segmentationFile instanceof File && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveFile}
                >
                  <X className="mr-2 h-4 w-4" />
                  Limpar import
                </Button>
              )}
            </div>

            {form.formState.errors.segmentationFile?.message && (
              <p className="text-sm text-red-500">
                {String(form.formState.errors.segmentationFile.message)}
              </p>
            )}

            {isLoading && (
              <p className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                Validando a base de CPFs...
              </p>
            )}

            <CpfSegmentationList cpfs={cpfs} onChange={setCpfs} />

            {report && !isLoading && (
              <div className="space-y-3 rounded-lg border p-4">
                <div className="flex flex-wrap gap-6 text-sm">
                  <span>
                    Último import: <strong>{report.total}</strong> linha(s)
                  </span>
                  <span className="text-green-700">
                    Válidos: <strong>{report.valid}</strong>
                  </span>
                  <span className="text-red-600">
                    Rejeitados: <strong>{report.invalid}</strong>
                  </span>
                  <span className="text-amber-700">
                    Duplicados: <strong>{report.duplicates}</strong>
                  </span>
                </div>

                {report.invalidRows.length > 0 && (
                  <>
                    <p className="flex items-center gap-2 text-sm font-medium text-amber-700">
                      <FileWarning className="h-4 w-4" />
                      Primeiras linhas rejeitadas
                    </p>
                    <div className="max-h-56 overflow-auto rounded border">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-left">
                          <tr>
                            <th className="px-3 py-2">Linha</th>
                            <th className="px-3 py-2">Valor</th>
                            <th className="px-3 py-2">Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {report.invalidRows
                            .slice(0, MAX_PREVIEW_INVALID_ROWS)
                            .map((row, index) => (
                              <tr key={`${row.line}-${index}`} className="border-t">
                                <td className="px-3 py-2">{row.line}</td>
                                <td className="px-3 py-2">{row.value}</td>
                                <td className="px-3 py-2">{row.reason}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {report.invalidRows.length > MAX_PREVIEW_INVALID_ROWS && (
                      <p className="text-xs text-gray-500">
                        Mostrando {MAX_PREVIEW_INVALID_ROWS} de{" "}
                        {report.invalidRows.length} linhas rejeitadas.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
